// Word-level diff between a finding's anchor and its (possibly edited)
// replacement, for the inline fix preview in ContentReviewPanel.
// Diffs are computed on plain text — markup is not diffed visually.
import { diffWords } from 'diff';

export function htmlToText(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

// Replace the first exact occurrence of anchorHtml in html with
// replacementHtml. Returns the new html, or null when the anchor no
// longer matches (stale finding) — never fuzzy-guesses a location.
export function applyFix(html, anchorHtml, replacementHtml) {
    if (!html || !anchorHtml) return null;
    const idx = html.indexOf(anchorHtml);
    if (idx === -1) return null;
    return html.slice(0, idx) + replacementHtml + html.slice(idx + anchorHtml.length);
}

// Apply fixes ({id, anchorHtml, replacementHtml}) in order, each to the
// result of the ones before. Returns the new html and the ids that
// applied; a fix whose anchor is gone (an earlier fix or an edit consumed
// it) is skipped, never guessed.
export function applyFixes(html, fixes) {
    let current = html;
    const appliedIds = [];
    for (const fix of fixes) {
        const updated = applyFix(current, fix.anchorHtml, fix.replacementHtml);
        if (updated === null) continue;
        current = updated;
        appliedIds.push(fix.id);
    }
    return { html: current, appliedIds };
}

// A changed segment's edge spaces move out into plain text, so a strike or
// highlight never covers a bare space.
function splitEdgeSpaces({ type, text }) {
    if (type === 'same') return [{ type, text }];
    const [, lead, core, trail] = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
    return [
        { type: 'same', text: lead },
        { type, text: core },
        { type: 'same', text: trail },
    ].filter(seg => seg.text);
}

// Word diff in the style of `git diff --word-diff`, from jsdiff: words
// and punctuation are separate tokens, so a dropped comma marks only the
// comma, not the word it sits on.
//
// Returns [{type: 'same' | 'removed' | 'added', text}] segments. Each
// segment carries its own whitespace, so they render joined as-is; a
// whitespace-only change counts as same.
export function wordDiff(oldText, newText) {
    const segments = diffWords(oldText || '', newText || '').flatMap(change => splitEdgeSpaces({
        type: change.added ? 'added' : change.removed ? 'removed' : 'same',
        text: change.value,
    }));
    return segments.reduce((merged, seg) => {
        const last = merged[merged.length - 1];
        if (last?.type === seg.type) last.text += seg.text;
        else merged.push(seg);
        return merged;
    }, []);
}
